/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { initialPageLoadedPromise } from "utils/DocumentViewUtils";
import { providers } from "dc-core";
import { apiFactory } from "api/external/ApiFactory";
import { publicApiNames } from "publicApis/constants";
import { getVerticalScrollbarSize } from "selectors/pagedViewState";
import { getExpandedPanelWidth } from "utils/SkinnyRailUtils";
import { getBottomBannerConfig } from "selectors/bottomBoardSelectors";
import { getTotalBottomSpace } from "utils/PageUtils";
import { getViewPortHeight } from "selectors/documentViewSelectors";
import { getPagesViewTotalHeight, getPagesViewYPos } from "selectors/pagedViewState";
import { SignalDispatcher } from "dc-preview-utils/src/Utils/SignalDispatcher";
import { overlayEvents } from "overlays/OverlayEvents";
import { ZOOM_TRIGGER_TYPE } from "constants/enums";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";

export const getAvailableWidth = async stores => {
  let toolsBoardProvider;
  try {
    toolsBoardProvider = await providers["tools-board-provider"]();
  } catch (error) {
    toolsBoardProvider = null;
  }
  const lhpConfig = toolsBoardProvider?.getConfig() || {};
  const isLHPOpen = !!lhpConfig.id;

  const rhpApis = await apiFactory.getExposedApis("preview", [publicApiNames.RHP], true, true);
  const rhpConfig = (await rhpApis[publicApiNames.RHP].getConfig()) || {};
  const isRHPOpen = !!rhpConfig.dropinId;

  const availableWidth =
    window.innerWidth -
    (isLHPOpen ? 276 : 0) -
    getVerticalScrollbarSize(stores) -
    (isRHPOpen ? getExpandedPanelWidth(stores) : 0) -
    51; // 276: LHP Width, 51: Skinny Rail Width

  return availableWidth;
};

export const getIsAtDocumentEnd = stores => {
  const maxScrollPossible = Math.max(
    getPagesViewTotalHeight(stores) - (getViewPortHeight(stores) - getTotalBottomSpace(stores)),
    0
  );
  const scrolledHeight = getPagesViewYPos(stores);

  return scrolledHeight >= maxScrollPossible;
};

export const getInitialContext = async stores => {
  await initialPageLoadedPromise(stores);

  let toolsBoardProvider;
  try {
    toolsBoardProvider = await providers["tools-board-provider"]();
  } catch (error) {
    toolsBoardProvider = null;
  }
  const lhpConfig = toolsBoardProvider?.getConfig();
  const activeLHP = lhpConfig?.id || getQueryParameterFromUrl(window.location.href, "viewer!megaVerb") || null;
  const rhpApis = await apiFactory.getExposedApis("preview", [publicApiNames.RHP], true, true);
  const rhpConfig = await rhpApis[publicApiNames.RHP].getConfig();
  const activeRHP = rhpConfig?.dropinId || null;

  const availableWidth = await getAvailableWidth(stores);

  const { isSharedFile } = getBottomBannerConfig(stores) || {};

  return {
    lhp: activeLHP,
    rhp: activeRHP,
    isSharedFile,
    availableWidth
  };
};

export const bottomBannerEvents = new SignalDispatcher();

const updateContextProperty = (eventName, eventData = {}) => {
  bottomBannerEvents.trigger("contextChange", { eventName, eventData });
};

export const handleLHPChange = async (boolean, stores) => {
  const method = boolean ? "subscribe" : "unsubscribe";

  let toolsBoardProvider;
  try {
    toolsBoardProvider = await providers["tools-board-provider"]();
  } catch (error) {
    return;
  }

  toolsBoardProvider[method]("bottom-banner", async val => {
    const config = toolsBoardProvider.getConfig() || {};

    const currentActiveDropin = config?.id || null;
    const previousActiveDropin = val?.id || null;

    if (currentActiveDropin !== previousActiveDropin) {
      const availableWidth = await getAvailableWidth(stores);
      updateContextProperty("LHPChange", { currentActiveDropin, previousActiveDropin, availableWidth });
    }
  });
};

export const handleRHPChange = async (boolean, stores) => {
  const method = boolean ? "subscribe" : "unsubscribe";

  const rhpApis = await apiFactory.getExposedApis("preview", [publicApiNames.RHP], true, true);

  rhpApis[publicApiNames.RHP][method](async val => {
    const { currentActiveDropin, previousActiveDropin } = val;

    if (currentActiveDropin !== previousActiveDropin) {
      const availableWidth = await getAvailableWidth(stores);
      updateContextProperty("RHPChange", { currentActiveDropin, previousActiveDropin, availableWidth });
    }
  });
};

export const handleRHPResize = async (boolean, stores) => {
  const method = boolean ? "customEventsSubscribe" : "customEventsUnsubscribe";
  const rhpRef = stores.appStore.rhpRefValue;

  rhpRef &&
    rhpRef.apiService.registeredPublicApis[method](async ({ isRHPResizing }) => {
      if (isRHPResizing) {
        updateContextProperty("resizeRHP", { isRHPResizing });
      } else {
        const availableWidth = await getAvailableWidth(stores);
        updateContextProperty("resizeRHP", { isRHPResizing, availableWidth });
      }
    }, "resizeRHP");
};

const handleZoomChange = (boolean, stores) => {
  const method = boolean ? "on" : "unregister";

  overlayEvents[method]("zoom", (_, triggerType) => {
    stores.bottomBoard.setIsSyntheticScroll(true);
    if ([ZOOM_TRIGGER_TYPE.NAV_BAR, ZOOM_TRIGGER_TYPE.PINCH_ZOOM, ZOOM_TRIGGER_TYPE.KEYBOARD].includes(triggerType)) {
      updateContextProperty("zoom");
    }
  });
};

const handleScrollChange = (boolean, stores = {}) => {
  const method = boolean ? "on" : "unregister";

  overlayEvents[method]("pagesViewScrolled", () => {
    if (!stores.bottomBoard.isSyntheticScroll) {
      updateContextProperty("pagesViewScrolled");
    }
  });

  overlayEvents[method]("pagesViewScrollEnd", () => {
    if (!stores.bottomBoard.isSyntheticScroll) {
      const isAtDocumentEnd = getIsAtDocumentEnd(stores);

      updateContextProperty("pagesViewScrollEnd", { isAtDocumentEnd });
    } else {
      stores.bottomBoard.setIsSyntheticScroll(false);
    }
  });
};

const handleSelectionChange = (boolean, stores) => {
  const method = boolean ? "on" : "unregister";

  overlayEvents[method]("selectTextToPoint", () => {
    updateContextProperty("selectTextToPoint");
  });

  overlayEvents[method]("selectionclear", () => {
    updateContextProperty("selectionclear");
  });
};

export const registerEventHandler = (config, stores) => {
  const handlers = {
    hideAndOpenOnLHP: handleLHPChange,
    hideAndOpenOnRHP: handleRHPChange,
    hideAndOpenOnRHPResize: handleRHPResize,
    hideAndOpenOnZoom: handleZoomChange,
    hideAndOpenOnScroll: handleScrollChange,
    hideOnSelection: handleSelectionChange
  };

  Object.keys(config).forEach(key => handlers[key] && handlers[key](config[key], stores));
};
